import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import {
  BLUEPRINTS_CONFIG,
  getBlueprintVettingRows,
  isBlueprintAgentTask,
  isBlueprintGenerating,
  isBlueprintPublished,
} from "constants/index";
import { useBlueprintGenerationTasksContext, useLinks, useMissionRoleTemplates } from "hooks";
import { formatDate, formatElapsedTime } from "utils";
import { colors, fontSize, margins } from "style";
import DataTable from "components/DataTable";
import { ActionButton, BUTTON_SIZE, DestructableButton } from "components/Buttons";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { PrimaryLink } from "components/Links";
import { Row } from "components/Containers";
import Tags, { TAG_SIZE } from "components/Tags";
import { TAG_COLORS } from "components/Tags/Tag";
import Tooltip from "components/Tooltip";
import Status from "components/Status";
import { InlineProgressBar } from "components/Progress";
import { BlueprintEditModal, BlueprintGenerationIndicator } from "components/Blueprints";

/**
 * BlueprintsList
 *
 * Every blueprint Talent Simple customers can pick from, with edit and delete actions.
 */
const BlueprintsList = (props) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const { handleRemoveTemplate, removingTemplate, handleTogglePublished, togglingPublished } = useMissionRoleTemplates({
    skip: true,
  });
  const links = useLinks();
  const { templates, loading, error, excludeTemplate } = useBlueprintGenerationTasksContext();

  const handleRowEdit = (template) => {
    setSelectedTemplate(template);
    setShowEditForm(true);
  };

  const handleRemove = (template) =>
    handleRemoveTemplate({ template, onCompleted: () => excludeTemplate(template.id) });

  const rowOptions = (template) => [
    {
      label: <FormattedMessage id={`Blueprints.List.${isBlueprintPublished(template) ? "Unpublish" : "Publish"}`} />,
      icon: isBlueprintPublished(template) ? ICON_TYPE.eyeBlocked : ICON_TYPE.eye,
      onClick: () => handleTogglePublished(template),
      disabled: togglingPublished,
    },
    {
      label: <FormattedMessage id="Global.Edit" />,
      icon: ICON_TYPE.edit,
      onClick: () => handleRowEdit(template),
    },
    {
      label: <FormattedMessage id="Global.Delete" />,
      icon: ICON_TYPE.trash,
      onClick: () => handleRemove(template),
      disabled: removingTemplate,
    },
  ];

  return (
    <>
      <DataTable
        data={templates}
        loading={loading}
        error={error}
        columns={COLUMNS({ links, onRemove: handleRemove, removing: removingTemplate })}
        rowOptions={(template) => (isBlueprintAgentTask(template) ? [] : rowOptions(template))}
        emptyMessage={{
          title: "Blueprints.List.Empty.Title",
          description: "Blueprints.List.Empty.Description",
        }}
        {...props}
      />
      <BlueprintEditModal template={selectedTemplate} show={showEditForm} onClose={() => setShowEditForm(false)} />
    </>
  );
};

const BLUEPRINT_CELLS = {
  name: (template, { links }) => (
    <PrimaryLink to={links.settings_blueprint(template.uuid)} routerEnabled>
      {template.name}
    </PrimaryLink>
  ),
  status: (template) => (
    <Status enabled={isBlueprintPublished(template)}>
      <FormattedMessage id={`Blueprints.List.${isBlueprintPublished(template) ? "Published" : "Unpublished"}`} />
    </Status>
  ),
  disciplines: (template) => (
    <Tags
      data={template.disciplines}
      limit={BLUEPRINTS_CONFIG.LIST_TAGS_LIMIT}
      size={TAG_SIZE.small}
      {...TAG_COLORS.purple}
    />
  ),
  skills: (template) => (
    <Tags
      data={template.primarySkills}
      limit={BLUEPRINTS_CONFIG.LIST_TAGS_LIMIT}
      size={TAG_SIZE.small}
      {...TAG_COLORS.pink}
    />
  ),
  vetting: (template) => (
    <Row gap={margins.xxsmall} noWrap>
      {getBlueprintVettingRows(template).map(({ id, icon, count }) => (
        <Tooltip key={id} tooltipId={id} values={{ count: count || 0 }} hideIcon>
          <span css={styles.vetting_count(count > 0)}>
            <Icon type={icon} size={ICON_SIZE.medium} color="inherit" />
            {count || 0}
          </span>
        </Tooltip>
      ))}
    </Row>
  ),
  updated: (template) => formatDate(template.updatedAt),
  action: (template, { links }) => (
    <PrimaryLink to={links.settings_blueprint(template.uuid)} routerEnabled>
      <ActionButton>
        <Icon type={ICON_TYPE.eye} size={ICON_SIZE.large} color={colors.purpleRainBase} />
      </ActionButton>
    </PrimaryLink>
  ),
};

const AGENT_TASK_CELLS = {
  name: (template) => (
    <Row gap={margins.xxsmall} noWrap>
      <BlueprintGenerationIndicator template={template} />
      <span>{template.name}</span>
    </Row>
  ),
  status: (template) =>
    isBlueprintGenerating(template) ? (
      <Status color={colors.purpleRainBase} background={colors.purpleRainLight3} dotColor={colors.purpleRainBase}>
        <FormattedMessage id="Blueprints.List.Generating" />
      </Status>
    ) : (
      <Status enabled={false}>
        <FormattedMessage id="Blueprints.List.GenerationFailed" />
      </Status>
    ),
  disciplines: () => null,
  skills: (template) =>
    isBlueprintGenerating(template) && (
      <span css={styles.step}>
        {template.latestBlueprintGenerationAgentTask.agentStatus || <FormattedMessage id="Blueprints.List.Starting" />}
      </span>
    ),
  vetting: (template) =>
    isBlueprintGenerating(template) && (
      <InlineProgressBar
        completed={template.latestBlueprintGenerationAgentTask.progress?.stepsCompleted || 0}
        total={template.latestBlueprintGenerationAgentTask.progress?.stepsTotal || 0}
      />
    ),
  updated: (template) =>
    isBlueprintGenerating(template) ? (
      <span css={styles.step}>
        <FormattedMessage
          id="Blueprints.List.Elapsed"
          values={{ elapsed: formatElapsedTime(template.latestBlueprintGenerationAgentTask.createdAt) }}
        />
      </span>
    ) : (
      formatDate(template.updatedAt)
    ),
  action: (template, { onRemove, removing }) => (
    <Tooltip tooltipId="Blueprints.List.Discard" hideIcon>
      <DestructableButton size={BUTTON_SIZE.small} onClick={() => onRemove(template)} disabled={removing}>
        <Icon type={ICON_TYPE.trash} color={colors.white} />
      </DestructableButton>
    </Tooltip>
  ),
};

const renderCell = (key, options) => (template) =>
  (isBlueprintAgentTask(template) ? AGENT_TASK_CELLS : BLUEPRINT_CELLS)[key](template, options);

const COLUMNS = (options) => [
  { label: <FormattedMessage id="Blueprints.List.ColumnName" />, cell: renderCell("name", options), width: "22%" },
  { label: <FormattedMessage id="Blueprints.List.ColumnStatus" />, cell: renderCell("status", options), width: "12%" },
  {
    label: <FormattedMessage id="Blueprints.List.ColumnDisciplines" />,
    cell: renderCell("disciplines", options),
    width: "10%",
  },
  { label: <FormattedMessage id="Blueprints.List.ColumnSkills" />, cell: renderCell("skills", options), width: "17%" },
  {
    label: <FormattedMessage id="Blueprints.List.ColumnVetting" />,
    cell: renderCell("vetting", options),
    width: "15%",
  },
  {
    label: <FormattedMessage id="Blueprints.List.ColumnUpdated" />,
    cell: renderCell("updated", options),
    width: "12%",
  },
  { cell: renderCell("action", options), width: "5%" },
];

const styles = {
  step: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
  vetting_count: (hasAny) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: ${hasAny ? colors.grayAnatomyBase : colors.grayAnatomyLight1};
  `,
};

export default BlueprintsList;
